import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import MonitoringShell from "../../../components/AiVisibility/Monitoring/MonitoringShell";
import MonitoringLanding from "../../../components/AiVisibility/Monitoring/MonitoringLanding";
import MonitoringOverview from "../../../components/AiVisibility/Monitoring/MonitoringOverview";
import MonitoringHistory from "../../../components/AiVisibility/Monitoring/MonitoringHistory";
import MonitoringPrompts from "../../../components/AiVisibility/Monitoring/MonitoringPrompts";
import MonitoringAnalysis from "../../../components/AiVisibility/Monitoring/MonitoringAnalysis";
import MonitoringCompetitors from "../../../components/AiVisibility/Monitoring/MonitoringCompetitors";
import MonitoringPromptResearch from "../../../components/AiVisibility/Monitoring/MonitoringPromptResearch";
import MonitoringAiTraffic from "../../../components/AiVisibility/Monitoring/MonitoringAiTraffic";
import MonitoringAlerts from "../../../components/AiVisibility/Monitoring/MonitoringAlerts";
import MonitoringSettings from "../../../components/AiVisibility/Monitoring/MonitoringSettings";
import { MONITORING_TABS } from "../../../components/AiVisibility/Monitoring/monitoringMock";
import { adaptProject } from "../../../components/AiVisibility/Monitoring/monitoringAdapters";
import {
	enableMonitoring,
	getMonitoringProject,
} from "../../../services/monitoringApi";
import { safeReturnTo } from "../../../components/AiVisibility/auditUtils";

const MONITORING_BASE = "/aivisibility/monitoring";
const CONTENT_STUDIO_MONITORING_WRITING =
	"/aivisibility/writing-assistance/monitoring";
const VALID_SECTIONS = new Set(
	MONITORING_TABS.filter((tab) => !tab.disabled).map((tab) => tab.id)
);

function getSectionFromPath(pathname) {
	if (!pathname.startsWith(MONITORING_BASE)) return "overview";
	const rest = pathname.slice(MONITORING_BASE.length).replace(/^\//, "");
	if (!rest) return null;
	const section = rest.split("/")[0];
	return VALID_SECTIONS.has(section) ? section : "overview";
}

function getSectionLabel(sectionId) {
	return MONITORING_TABS.find((tab) => tab.id === sectionId)?.label || "Overview";
}

function brandFromWebsite(website) {
	const raw =
		website?.websiteURL ||
		website?.websiteUrl ||
		website?.websiteName ||
		"";
	if (!raw) return undefined;
	try {
		const host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname;
		return host.replace(/^www\./i, "") || undefined;
	} catch {
		return String(raw)
			.replace(/^https?:\/\//i, "")
			.replace(/\/.*$/, "")
			.replace(/^www\./i, "") || undefined;
	}
}

function MonitoringSectionPanel({
	activeSection,
	websiteId,
	project,
	onOpenWritingRun,
	promptsRefreshSeq,
	onPromptAnalysisSettled,
	onPromptNavLockChange,
}) {
	switch (activeSection) {
		case "overview":
			return <MonitoringOverview websiteId={websiteId} />;
		case "history":
			return <MonitoringHistory websiteId={websiteId} />;
		case "prompts":
			return (
				<MonitoringPrompts
					websiteId={websiteId}
					refreshSeq={promptsRefreshSeq}
					onNavLockChange={onPromptNavLockChange}
				/>
			);
		case "analysis":
			return (
				<MonitoringAnalysis
					websiteId={websiteId}
					onOpenWritingRun={onOpenWritingRun}
					onAnalysisSettled={onPromptAnalysisSettled}
				/>
			);
		case "competitors":
			return <MonitoringCompetitors websiteId={websiteId} />;
		case "prompt-research":
			return <MonitoringPromptResearch websiteId={websiteId} />;
		case "ai-traffic":
			return <MonitoringAiTraffic websiteId={websiteId} />;
		case "alerts":
			return <MonitoringAlerts websiteId={websiteId} />;
		case "settings":
			return <MonitoringSettings websiteId={websiteId} />;
		default:
			return (
				<div className="monitoring__stub">
					<p className="monitoring__stub-title mb-1">Coming soon</p>
					<p className="text-muted mb-0">This section is not available yet.</p>
				</div>
			);
	}
}

const AiVisibilityMonitoring = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		currentWebsiteId,
		userLoader,
		websiteLoader,
		websiteData,
		monitoringEnabled,
		setMonitoringEnabled,
	} = useContext(ProductContext);

	// If layout already confirmed enabled, skip the initial full-page skeleton flash.
	const [gateLoading, setGateLoading] = useState(() => !monitoringEnabled);
	const [project, setProject] = useState(null);
	const [enableOpen, setEnableOpen] = useState(false);
	const [enableBusy, setEnableBusy] = useState(false);
	const [enableError, setEnableError] = useState(null);
	const projectRef = useRef(project);
	projectRef.current = project;

	const website = websiteData?.[currentWebsiteId];
	const websiteLabel = brandFromWebsite(website) || "";

	const loadProject = useCallback(
		async ({ keepGate = false } = {}) => {
			if (!currentWebsiteId) {
				setProject(null);
				setMonitoringEnabled(false);
				setGateLoading(false);
				return null;
			}
			// Keep existing UI visible while refreshing (section switches share one mount).
			if (!keepGate && !projectRef.current) setGateLoading(true);
			const res = await getMonitoringProject(currentWebsiteId);
			if (res.ok && res.project) {
				const adapted = adaptProject(res.project);
				setProject(adapted);
				setMonitoringEnabled(true);
				setGateLoading(false);
				return adapted;
			}
			setProject(null);
			setMonitoringEnabled(false);
			setGateLoading(false);
			return null;
		},
		[currentWebsiteId, setMonitoringEnabled]
	);

	useEffect(() => {
		setEnableOpen(false);
		setEnableError(null);
		loadProject();
	}, [loadProject]);

	const activeSection = useMemo(
		() => getSectionFromPath(location.pathname),
		[location.pathname]
	);

	const pageTitle = useMemo(() => {
		if (!activeSection) return "Monitoring - Overview";
		return `Monitoring - ${getSectionLabel(activeSection)}`;
	}, [activeSection]);

	const handleEnable = useCallback(async () => {
		if (!currentWebsiteId || enableBusy) return;
		setEnableBusy(true);
		setEnableError(null);
		const res = await enableMonitoring(currentWebsiteId);
		if (!res.ok) {
			setEnableBusy(false);
			setEnableError(res.message || "Failed to enable monitoring");
			return;
		}
		const seeded = res.result?.seeded_prompt_count ?? 0;
		const note = res.result?.note || null;
		toast.success(
			note
				? `Monitoring enabled (${seeded} prompts seeded). ${note}`
				: `Monitoring enabled — ${seeded} prompts seeded.`
		);
		const adapted = await loadProject({ keepGate: true });
		setEnableBusy(false);
		if (adapted) {
			setEnableOpen(false);
			navigate(`${MONITORING_BASE}/overview`, { replace: true });
		} else {
			setEnableError(
				"Monitoring was enabled but the project is not ready yet. Try again in a moment."
			);
		}
	}, [currentWebsiteId, enableBusy, loadProject, navigate]);

	const handleRefreshProject = useCallback(async () => {
		await loadProject();
	}, [loadProject]);

	const [promptsRefreshSeq, setPromptsRefreshSeq] = useState(0);
	const [monitoringNavLocked, setMonitoringNavLocked] = useState(false);
	const handlePromptAnalysisSettled = useCallback(() => {
		setPromptsRefreshSeq((n) => n + 1);
	}, []);

	const handleOpenWritingRun = useCallback(
		(runId, { returnTo } = {}) => {
			const params = new URLSearchParams();
			params.set("runId", String(runId));
			const safeBack = safeReturnTo(returnTo);
			if (safeBack) {
				params.set("returnTo", safeBack);
			}
			navigate(`${CONTENT_STUDIO_MONITORING_WRITING}?${params.toString()}`);
		},
		[navigate]
	);

	const isBaseRoute =
		location.pathname === MONITORING_BASE ||
		location.pathname === `${MONITORING_BASE}/`;

	// Redirect legacy Keywords tab to Prompt Research.
	if (location.pathname === `${MONITORING_BASE}/keywords`) {
		return <Navigate to={`${MONITORING_BASE}/prompt-research`} replace />;
	}

	// Redirect legacy monitoring writing-assistant URL to Content Studio.
	if (location.pathname === `${MONITORING_BASE}/writing-assistant`) {
		const next = new URLSearchParams(location.search);
		const query = next.toString();
		return (
			<Navigate
				to={
					query
						? `${CONTENT_STUDIO_MONITORING_WRITING}?${query}`
						: CONTENT_STUDIO_MONITORING_WRITING
				}
				replace
			/>
		);
	}

	if (userLoader || websiteLoader || gateLoading) {
		return <DetailSkeleton />;
	}

	// Not enabled → landing only (tabs hidden until project exists).
	if (!project) {
		// Layout already says enabled — wait for hydrate (avoid landing flash).
		if (monitoringEnabled) {
			return <DetailSkeleton />;
		}
		if (!isBaseRoute) {
			return <Navigate to={MONITORING_BASE} replace />;
		}
		return (
			<div className="contentBlock ai-visibility-dashboard monitoring-page">
				<MonitoringLanding
					websiteLabel={websiteLabel}
					websiteId={currentWebsiteId}
					enableOpen={enableOpen}
					onOpenEnable={() => {
						setEnableError(null);
						setEnableOpen(true);
					}}
					onCloseEnable={() => {
						if (enableBusy) return;
						setEnableOpen(false);
						setEnableError(null);
					}}
					onEnable={handleEnable}
					busy={enableBusy}
					error={enableError}
				/>
			</div>
		);
	}

	// B1: already enabled → auto-enter tabs (redirect bare /monitoring → overview).
	if (activeSection === null) {
		return <Navigate to={`${MONITORING_BASE}/overview`} replace />;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard monitoring-page">
			<MonitoringShell
				title={pageTitle}
				project={project}
				websiteId={currentWebsiteId}
				activeSection={activeSection}
				onProjectChange={handleRefreshProject}
				navLocked={monitoringNavLocked}
				onNavBlocked={() =>
					toast.info("Close or cancel the prompt dialog before switching tabs.")
				}
			>
				<MonitoringSectionPanel
					activeSection={activeSection}
					websiteId={currentWebsiteId}
					project={project}
					onOpenWritingRun={handleOpenWritingRun}
					promptsRefreshSeq={promptsRefreshSeq}
					onPromptAnalysisSettled={handlePromptAnalysisSettled}
					onPromptNavLockChange={setMonitoringNavLocked}
				/>
			</MonitoringShell>
		</div>
	);
};

export default AiVisibilityMonitoring;
